/**
 * 리치 텍스트 글자 색상 — 프리셋 팔레트 SSOT + 표시단 정규화 유틸.
 *
 * 설계: `docs/designs/rich-text-color.md`
 *
 * 저장 포맷은 마크다운 안 인라인 HTML 한 형태로 고정한다.
 *
 * ```
 * <span style="color: #dc2626">빨간 강조</span>
 * ```
 *
 * 색 값은 **닫힌 프리셋**만 허용한다. 임의 CSS 가 본문에 저장·표시되지 않도록
 * 편집기 파싱·직렬화, 표시 경로 sanitize 가 모두 이 파일의 `normalizeRichTextColor`
 * 하나를 통과 기준으로 쓴다.
 *
 * 이 파일은 tiptap 에 의존하지 않는다(표시 전용 화면에서도 import 가능).
 */

export interface AxRichTextColor {
  /** 프리셋 식별자. */
  key: string;
  /** 툴바 표시 이름. */
  label: string;
  /** 저장·표시에 쓰는 소문자 hex. */
  value: string;
}

/**
 * 프리셋 8색. 흰 배경 대비를 확보한 600 단계 위주이며 `indigo`·`gray` 는 각각
 * 디자인 토큰 `--ax-color-primary`·`--ax-color-secondary` 와 같은 값이다.
 *
 * 본문에 영구 저장되는 값이라 CSS 변수를 쓸 수 없다(PDF·모바일 앱·외부 붙여넣기에서
 * 해석되지 않음). 팔레트 변경 시 erp-be `vendor-contract-html.ts`,
 * erp-app `utils/markdown.ts` 의 복제본도 함께 고친다.
 */
export const AX_RICH_TEXT_COLORS: readonly AxRichTextColor[] = [
  { key: 'red', label: '빨강', value: '#dc2626' },
  { key: 'orange', label: '주황', value: '#ea580c' },
  { key: 'amber', label: '황토', value: '#ca8a04' },
  { key: 'green', label: '초록', value: '#16a34a' },
  { key: 'blue', label: '파랑', value: '#2563eb' },
  { key: 'indigo', label: '남색', value: '#4f46e5' },
  { key: 'purple', label: '보라', value: '#9333ea' },
  { key: 'gray', label: '회색', value: '#64748b' },
];

const ALLOWED_VALUES = new Set(AX_RICH_TEXT_COLORS.map((color) => color.value));

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/;
const RGB_RE = /^rgba?\(\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*[, ]\s*(\d{1,3})\s*(?:[,/][^)]*)?\)$/;

function expandShortHex(hex: string): string {
  if (hex.length !== 4) return hex;
  const [r, g, b] = [hex[1], hex[2], hex[3]];
  return `#${r}${r}${g}${g}${b}${b}`;
}

function rgbToHex(r: number, g: number, b: number): string | null {
  if ([r, g, b].some((n) => !Number.isInteger(n) || n < 0 || n > 255)) return null;
  return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

/** `style` 문자열에서 `color` 선언 값만 뽑는다. `background-color` 등은 무시. */
function extractColorDeclaration(style: string): string | null {
  for (const declaration of style.split(';')) {
    const separator = declaration.indexOf(':');
    if (separator < 0) continue;
    const property = declaration.slice(0, separator).trim().toLowerCase();
    if (property !== 'color') continue;
    return declaration.slice(separator + 1).trim();
  }
  return null;
}

/**
 * 색 표기(`#RGB`·`#RRGGBB`·`rgb()`·`color: …` 선언)를 프리셋 hex 로 정규화한다.
 * 프리셋 밖이면 `null` — 호출부는 이 경우 색을 버린다(태그 제거, 글자는 유지).
 */
export function normalizeRichTextColor(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const source = (raw.includes(':') ? extractColorDeclaration(raw) : raw)?.trim().toLowerCase();
  if (!source) return null;

  if (HEX_RE.test(source)) {
    const hex = expandShortHex(source);
    return ALLOWED_VALUES.has(hex) ? hex : null;
  }

  const rgb = RGB_RE.exec(source);
  if (rgb) {
    const hex = rgbToHex(Number(rgb[1]), Number(rgb[2]), Number(rgb[3]));
    return hex && ALLOWED_VALUES.has(hex) ? hex : null;
  }

  return null;
}

/** 저장·표시에 쓰는 여는 태그. 편집기 직렬화와 표시단 복원이 같은 문자열을 만든다. */
export function richTextColorOpenTag(color: string): string {
  return `<span style="color: ${color}">`;
}

/** 여는 태그 매칭용 — 속성은 `style` 하나, 값은 `color` 선언 하나만 허용한다. */
export const RICH_TEXT_COLOR_OPEN_TAG_RE =
  /^<span\s+style\s*=\s*"\s*color\s*:\s*([^";]+?)\s*;?\s*"\s*>/i;

export const RICH_TEXT_COLOR_CLOSE_TAG = '</span>';

/**
 * sanitize 된 HTML 에서 `style` 속성을 프리셋 색 하나로 좁힌다.
 *
 * DOMPurify 로 태그·속성을 거른 **뒤** 호출한다. 프리셋 색이면 `color` 선언만 남기고,
 * 아니면 속성을 통째로 제거한다(글자는 그대로 남는다).
 */
export function keepOnlyRichTextColorStyle(html: string): string {
  if (!html.includes('style')) return html;
  if (typeof DOMParser === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  for (const element of Array.from(doc.body.querySelectorAll('[style]'))) {
    const color = normalizeRichTextColor(element.getAttribute('style'));
    if (color) element.setAttribute('style', `color: ${color}`);
    else element.removeAttribute('style');
  }
  return doc.body.innerHTML;
}
